// src/app/core/navigation/navigation.service.ts

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { cloneDeep } from 'lodash-es';
import { Observable, of, ReplaySubject } from 'rxjs';

import { Navigation } from 'app/core/navigation/navigation.types';
import * as WildHarvestNav from 'app/core/navigation/data-wild-harvest';
import * as AquacultureNav from 'app/core/navigation/data-aquaculture';

import { CUSTOMER_CONFIG } from 'app/core/customer/customer.config';
import { AppKey, CustomerFeatureKey } from 'app/core/customer/customer-config.model';
import { AuthService } from 'app/core/auth/auth.service';

type CustomerNavItem = FuseNavigationItem & {
  featureKey?: CustomerFeatureKey;
  roles?: string[];
  meta?: {
    featureKey?: CustomerFeatureKey;
    roles?: string[];
    [key: string]: unknown;
  };
};

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

  constructor(
    private _router: Router,
    private _authService: AuthService,
  ) {}

  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  get(app?: AppKey): Observable<Navigation> {
    const resolvedApp = app ?? this._getCurrentApp();
    const navigation = this._buildNavigation(resolvedApp);
    this._navigation.next(navigation);

    return of(navigation);
  }

  private _getCurrentApp(): AppKey {
    const url = this._router.url || '';

    if (url.startsWith('/wild-harvest')) {
      return 'wild-harvest';
    }

    if (url.startsWith('/aquaculture')) {
      return 'aquaculture';
    }

    const stored = localStorage.getItem('currentApp') as AppKey | null;

    if (stored && CUSTOMER_CONFIG.apps[stored]) {
      return stored;
    }

    return CUSTOMER_CONFIG.defaultApp;
  }

  private _buildNavigation(app: AppKey): Navigation {
    const resolvedApp = CUSTOMER_CONFIG.apps[app] ? app : CUSTOMER_CONFIG.defaultApp;

    const isAquaculture = resolvedApp === 'aquaculture';
    const prefix = isAquaculture ? '/aquaculture' : '/wild-harvest';
    const baseNav = (isAquaculture ? AquacultureNav : WildHarvestNav) as {
      defaultNavigation?: FuseNavigationItem[];
      compactNavigation?: FuseNavigationItem[];
      futuristicNavigation?: FuseNavigationItem[];
      horizontalNavigation?: FuseNavigationItem[];
    };

    const defaultSource = baseNav.defaultNavigation ?? [];
    const compactSource = baseNav.compactNavigation ?? defaultSource;
    const futuristicSource = baseNav.futuristicNavigation ?? defaultSource;
    const horizontalSource = baseNav.horizontalNavigation ?? defaultSource;

    const defaultNav = this._filterByCustomerFeatures(this._prefixLinks(defaultSource, prefix));
    const compactNav = this._filterByCustomerFeatures(this._prefixLinks(compactSource, prefix));
    const futuristicNav = this._filterByCustomerFeatures(this._prefixLinks(futuristicSource, prefix));
    const horizontalNav = this._filterByCustomerFeatures(this._prefixLinks(horizontalSource, prefix));

    const filled = this._fillChildrenFromDefault(compactNav, futuristicNav, horizontalNav, defaultNav);

    return {
      default: cloneDeep(defaultNav),
      compact: filled.compact,
      futuristic: filled.futuristic,
      horizontal: filled.horizontal,
    };
  }

  private _prefixLinks(items: FuseNavigationItem[], prefix: string): FuseNavigationItem[] {
    const nav = cloneDeep(items);

    const applyPrefix = (nodes: FuseNavigationItem[]): void => {
      for (const node of nodes) {
        if (node.link && node.link.startsWith('/')) {
          node.link = `${prefix}${node.link}`;
        }

        if (node.children?.length) {
          applyPrefix(node.children);
        }
      }
    };

    applyPrefix(nav);
    return nav;
  }

  private _filterByCustomerFeatures(items: FuseNavigationItem[]): FuseNavigationItem[] {
    const userRoles: string[] = this._authService.userData?.roles ?? [];
    const isAdmin = userRoles.includes('ROLE_ADMIN');

    const filterNode = (node: FuseNavigationItem): FuseNavigationItem | null => {
      const customerNode = node as CustomerNavItem;

      const featureKey = customerNode.featureKey ?? customerNode.meta?.featureKey;
      const requiredRoles = customerNode.roles ?? customerNode.meta?.roles ?? [];

      const isClickableItem = !!node.link && node.type === 'basic';

      // Feature disabled → hide
      if (featureKey && !CUSTOMER_CONFIG.enabledFeatures.includes(featureKey)) {
        return null;
      }

      // Clickable item without feature key → hide for safety
      if (isClickableItem && !featureKey) {
        return null;
      }

      // Admin sees everything enabled
      if (!isAdmin && isClickableItem) {
        // Empty/missing roles = admin-only
        if (!requiredRoles.length) {
          return null;
        }

        const hasRole = requiredRoles.some((role: string) => userRoles.includes(role));

        if (!hasRole) {
          return null;
        }
      }

      const cloned = cloneDeep(node);

      if (cloned.children?.length) {
        cloned.children = cloned.children.map((child) => filterNode(child)).filter((child): child is FuseNavigationItem => !!child);
      }

      if (cloned.type === 'collapsable' && (!cloned.children || cloned.children.length === 0)) {
        return null;
      }

      return cloned;
    };

    return items.map((item) => filterNode(item)).filter((item): item is FuseNavigationItem => !!item);
  }

  private _fillChildrenFromDefault(
    compact: FuseNavigationItem[],
    futuristic: FuseNavigationItem[],
    horizontal: FuseNavigationItem[],
    defaultNav: FuseNavigationItem[],
  ): {
    compact: FuseNavigationItem[];
    futuristic: FuseNavigationItem[];
    horizontal: FuseNavigationItem[];
  } {
    const compactFilled = cloneDeep(compact);
    const futuristicFilled = cloneDeep(futuristic);
    const horizontalFilled = cloneDeep(horizontal);

    const attachChildren = (targets: FuseNavigationItem[]): void => {
      for (const target of targets) {
        const match = this._findById(defaultNav, target.id);
        if (match?.children?.length) {
          target.children = cloneDeep(match.children);
        }
      }
    };

    attachChildren(compactFilled);
    attachChildren(futuristicFilled);
    attachChildren(horizontalFilled);

    return {
      compact: compactFilled,
      futuristic: futuristicFilled,
      horizontal: horizontalFilled,
    };
  }

  private _findById(items: FuseNavigationItem[], id?: string): FuseNavigationItem | null {
    if (!id) {
      return null;
    }

    for (const item of items) {
      if (item.id === id) {
        return item;
      }

      if (item.children?.length) {
        const found = this._findById(item.children, id);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }
}
